import {
    Autocomplete,
    type AutocompleteProps,
    type AutocompleteRenderInputParams,
    type AutocompleteRenderOptionState,
    type TextFieldProps,
    TextField,
    MenuList,
    MenuItem,
    ListItemText,
} from '@mui/material'
import {
    Children,
    createRef,
    forwardRef,
    useRef,
    type CSSProperties,
    type HTMLAttributes,
    type ReactElement,
    type ReactNode,
} from 'react'
import { VariableSizeList } from 'react-window'
import Measure from 'react-measure'

export interface SearchableSelectOption {
    label: string
    value: string
}

export interface SearchableSelectProps<
    T extends SearchableSelectOption,
    M extends boolean,
> extends Omit<
    AutocompleteProps<T, M, true, false>,
    'freeSolo' | 'options' | 'renderInput'
> {
    options: T[]
    rowHeight?: number
    TextFieldProps?: Partial<TextFieldProps>
    renderInput?: (params: AutocompleteRenderInputParams) => ReactElement
    multiple?: M
}

/**
 * This component renders a searchable select dropdown using MUI's Autocomplete component,
 * with virtualization for improved performance with large data.
 * 
 * @param {T} options           All the options available for selection in the dropdown.
 * @param {number} [rowHeight=34]   The height of each row in the dropdown list.
 * @param {Partial<TextFieldProps>} [TextFieldProps={}] Additional properties to customize the TextField component used for input.
 * @param {(params: AutocompleteRenderInputParams) => ReactElement} [renderInput] Custom function to render the input field.
 * @param {M} multiple          Whether multiple selections are allowed.
 * 
 * @returns {ReactElement}  A searchable select dropdown component.
 * 
 * @example
 * ```tsx
 * const options = [
 *   { label: 'Option 1', value: '1' },
 *   { label: 'Option 2', value: '2' },
 *   // ...more options
 * ];
 * 
 * <SearchableSelect
 *   options={options}
 * />
 * ```
 */
export const SearchableSelect = <
    K extends SearchableSelectOption,
    M extends boolean,
>({
    options,
    rowHeight = 34,
    TextFieldProps = {},
    renderInput,
    renderOption,
    ...props
}: SearchableSelectProps<K, M>) => {
    const itemHeights = useRef<{ [key: number]: number }>({})
    const listRef = createRef<VariableSizeList>()

    const renderInputDefault = (params: AutocompleteRenderInputParams) => {
        return (
            <TextField
                autoFocus
                {...params}
                {...TextFieldProps}
                variant="standard"
            />
        )
    }

    const renderOptionDefault = (
        props: HTMLAttributes<HTMLLIElement>,
        option: SearchableSelectOption,
        state: AutocompleteRenderOptionState,
    ) => {
        return (
            <MenuItem value={option.value} {...props} key={state.index}>
                <ListItemText
                    primary={option.label}
                    sx={{ textWrap: 'auto' }}
                />
            </MenuItem>
        )
    }

    const ListboxComponent = forwardRef<
        HTMLDivElement,
        HTMLAttributes<HTMLElement>
    >(function ListboxComponent(props, ref) {
        const { children, ...other } = props
        const items: ReactNode[] = Children.toArray(children)
        const renderRow = ({
            index,
            style,
        }: {
            index: number
            style: CSSProperties
        }) => {
            return (
                <div key={index} style={style}>
                    <Measure
                        client
                        offset
                        scroll
                        bounds
                        margin
                        onResize={(contentRect) => {
                            const newHeight = contentRect.offset
                                ?.height as number
                            itemHeights.current = {
                                ...itemHeights.current,
                                [index]: newHeight,
                            }
                            listRef.current?.resetAfterIndex(0)
                        }}>
                        {({
                            measureRef,
                        }: {
                            measureRef: (
                                instance: HTMLDivElement | null,
                            ) => void
                        }) => <div ref={measureRef}>{items[index]}</div>}
                    </Measure>
                </div>
            )
        }

        const getItemSize = (index: number) =>
            itemHeights.current[index] ?? rowHeight

        return (
            <div ref={ref} {...other}>
                <MenuList>
                    <VariableSizeList
                        itemData={items}
                        itemSize={getItemSize}
                        height={600}
                        width="100%"
                        innerElementType="ul"
                        overscanCount={5}
                        itemCount={items.length}
                        ref={listRef}>
                        {renderRow as any}
                    </VariableSizeList>
                </MenuList>
            </div>
        )
    })

    return (
        <Autocomplete
            disableListWrap
            autoFocus
            fullWidth
            disableClearable
            freeSolo={false}
            options={options}
            inputMode="search"
            renderInput={renderInput ?? renderInputDefault}
            renderOption={renderOption ?? renderOptionDefault}
            slots={{ listbox: ListboxComponent }}
            isOptionEqualToValue={(option, value) =>
                option.value === value.value
            }
            {...props}
        />
    )
}